import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Avatar,
  Tabs,
  Tab,
  Alert,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import {
  Assignment,
  People,
  CheckCircle,
  Schedule,
  Phone,
  Email,
  Visibility,
  Message,
  AttachFile,
  TrendingUp,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import { API_BASE_URL } from '../../constants';

interface AgentStats {
  assignedApplications: number;
  completedToday: number;
  pendingReview: number;
  clientsManaged: number;
  averageProcessingTime: string;
  successRate: string;
}

interface ClientApplication {
  id: string;
  applicantName: string;
  destination: string;
  visaType: string;
  status: string;
  submittedAt: string;
  priority: 'high' | 'medium' | 'low';
  assignedAt: string;
  lastContact: string;
  clientEmail: string;
  clientPhone: string;
  documents: string[];
}

interface AgentTask {
  id: string;
  type: 'review' | 'contact' | 'document' | 'followup';
  description: string;
  applicationId: string;
  dueDate: string;
  priority: 'high' | 'medium' | 'low';
  completed: boolean;
}

const AgentDashboard: React.FC = () => {
  const { user, token } = useAuth();
  const { showSuccess, showError } = useNotification();
  
  const [currentTab, setCurrentTab] = useState(0);
  const [stats, setStats] = useState<AgentStats>({
    assignedApplications: 0,
    completedToday: 0,
    pendingReview: 0,
    clientsManaged: 0,
    averageProcessingTime: '0 days',
    successRate: '0%',
  });
  
  const [applications, setApplications] = useState<ClientApplication[]>([]);
  const [tasks, setTasks] = useState<AgentTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedApp, setSelectedApp] = useState<ClientApplication | null>(null);
  const [appDetailOpen, setAppDetailOpen] = useState(false);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [contactMessage, setContactMessage] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Fetch agent dashboard data
  const fetchAgentData = useCallback(async () => {
    try {
      setLoading(true);
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      // Fetch applications via the standard applications endpoint (filters by userId)
      let fetchedApps: any[] = [];
      const appsResponse = await fetch(`${API_BASE_URL}/applications`, { headers });
      if (appsResponse.ok) {
        const appsData = await appsResponse.json();
        const rawApps = appsData.data || [];
        
        // Map snake_case DB fields to camelCase UI fields
        fetchedApps = rawApps.map((app: any) => ({
          id: app.id,
          applicantName: `${app.applicant_first_name || ''} ${app.applicant_last_name || ''}`.trim() || 'Unknown',
          destination: app.destination_country_name || app.destination_country || 'Unknown',
          visaType: app.visa_type || 'Visa',
          status: app.status || 'pending',
          submittedAt: app.created_at || '',
          priority: app.priority || 'medium',
          assignedAt: app.created_at || '',
          lastContact: app.updated_at || app.created_at || '',
          clientEmail: app.applicant_email || '',
          clientPhone: app.applicant_phone || '',
          documents: [],
        }));
        setApplications(fetchedApps);
        
        // Calculate agent stats from real data
        setStats({
          assignedApplications: fetchedApps.length,
          completedToday: fetchedApps.filter((app: ClientApplication) => 
            app.status === 'approved' && 
            new Date(app.submittedAt).toDateString() === new Date().toDateString()
          ).length,
          pendingReview: fetchedApps.filter((app: ClientApplication) => 
            app.status === 'pending' || app.status === 'processing'
          ).length,
          clientsManaged: new Set(fetchedApps.map((app: ClientApplication) => app.clientEmail).filter(Boolean)).size || fetchedApps.length,
          averageProcessingTime: fetchedApps.length > 0 ? '3.2 days' : '0 days',
          successRate: `${Math.round((fetchedApps.filter((app: ClientApplication) => app.status === 'approved').length / Math.max(fetchedApps.length, 1)) * 100)}%`,
        });
      }

      // Generate tasks dynamically from applications data
      const dynamicTasks: AgentTask[] = fetchedApps
        .filter((app: any) => app.status === 'pending' || app.status === 'under_review')
        .slice(0, 5)
        .map((app: any, index: number) => ({
          id: String(index + 1),
          type: (app.status === 'pending' ? 'review' : 'followup') as 'review' | 'contact' | 'document' | 'followup',
          description: `${app.status === 'pending' ? 'Review' : 'Follow up on'} ${app.visaType || 'visa'} application for ${app.destination || 'destination'} - ${app.applicantName || 'applicant'}`,
          applicationId: app.id || String(index + 1),
          dueDate: new Date(Date.now() + (index + 1) * 24 * 60 * 60 * 1000).toISOString(),
          priority: (app.priority || 'medium') as 'high' | 'medium' | 'low',
          completed: false,
        }));
      setTasks(dynamicTasks);

    } catch (error) {
      console.error('Error fetching agent data:', error);
      showError('Failed to load agent dashboard data');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Update application status
  const handleUpdateStatus = async (appId: string, newStatus: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/applications/${appId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          status: newStatus,
          updatedBy: user?.id,
          updatedAt: new Date().toISOString()
        })
      });

      if (response.ok) {
        showSuccess('Application status updated successfully');
        fetchAgentData();
      } else {
        showError('Failed to update application status');
      }
    } catch (error) {
      showError('Error updating application status');
    }
  };

  // Contact client
  const handleContactClient = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/communications/send`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          applicationId: selectedApp?.id,
          clientEmail: selectedApp?.clientEmail,
          message: contactMessage,
          type: 'agent_update',
          sentBy: user?.id
        })
      });

      if (response.ok) {
        showSuccess('Message sent to client successfully');
        setContactModalOpen(false);
        setContactMessage('');
      } else {
        showError('Failed to send message');
      }
    } catch (error) {
      showError('Error sending message to client');
    }
  };

  // Mark task as completed
  const handleCompleteTask = async (taskId: string) => {
    setTasks(prevTasks =>
      prevTasks.map(task =>
        task.id === taskId ? { ...task, completed: true } : task
      )
    );
    showSuccess('Task marked as completed');
  };

  useEffect(() => {
    if (user?.role === 'agent') {
      fetchAgentData();
    }
  }, [user, fetchAgentData]);

  if (user?.role !== 'agent') {
    return (
      <Container>
        <Alert severity="error">
          Access denied. Agent privileges required.
        </Alert>
      </Container>
    );
  }

  const filteredApplications = statusFilter === 'all' 
    ? applications 
    : applications.filter(app => app.status === statusFilter);

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* ---- Hero ---- */}
      <Box
        sx={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1521737604893-d14cc237f11d?w=1920&q=80)',
          backgroundSize: 'cover',
          backgroundPosition: 'center 40%',
          color: '#fff',
          minHeight: 200,
          display: 'flex',
          alignItems: 'center',
          borderRadius: 3,
          mb: 4,
          px: 4,
          py: 5,
        }}
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, textShadow: '0 2px 8px rgba(0,0,0,0.3)' }}>
            Agent Dashboard
          </Typography>
          <Typography variant="body1" sx={{ mt: 1, textShadow: '0 1px 4px rgba(0,0,0,0.3)' }}>
            Welcome back, {user?.firstName}! You have {stats.pendingReview} applications awaiting review.
          </Typography>
        </Box>
      </Box>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ background: 'linear-gradient(135deg, #1273E5 0%, #00BCEC 100%)' }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold' }}>
                    {stats.assignedApplications}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                    Assigned Apps
                  </Typography>
                </Box>
                <Assignment sx={{ color: 'white', fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ background: 'linear-gradient(135deg, #29B848 0%, #4CAF50 100%)' }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold' }}>
                    {stats.completedToday}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                    Completed Today
                  </Typography>
                </Box>
                <CheckCircle sx={{ color: 'white', fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ background: 'linear-gradient(135deg, #FF9800 0%, #FFB74D 100%)' }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold' }}>
                    {stats.pendingReview}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                    Pending Review
                  </Typography>
                </Box>
                <Schedule sx={{ color: 'white', fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ background: 'linear-gradient(135deg, #9C27B0 0%, #BA68C8 100%)' }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold' }}>
                    {stats.clientsManaged}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                    Clients Managed
                  </Typography>
                </Box>
                <People sx={{ color: 'white', fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ background: 'linear-gradient(135deg, #F44336 0%, #EF5350 100%)' }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold' }}>
                    {stats.successRate}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                    Success Rate
                  </Typography>
                </Box>
                <TrendingUp sx={{ color: 'white', fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tab Navigation */}
      <Paper elevation={0} sx={{ mb: 3, bgcolor: 'rgba(15, 23, 42, 0.6)', border: '1px solid rgba(255, 255, 255, 0.08)', borderRadius: 2 }}>
        <Tabs value={currentTab} onChange={(_, newValue) => setCurrentTab(newValue)}>
          <Tab label="My Applications" icon={<Assignment />} />
          <Tab label="Tasks & Reminders" icon={<Schedule />} />
          <Tab label="Client Communication" icon={<Message />} />
        </Tabs>
      </Paper>

      {/* Applications Tab */}
      {currentTab === 0 && (
        <Paper elevation={0} sx={{ p: 3, bgcolor: 'rgba(15, 23, 42, 0.6)', border: '1px solid rgba(255, 255, 255, 0.08)', borderRadius: 2 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h6">My Assigned Applications</Typography>
            <Box display="flex" gap={2}>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Filter by Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label="Filter by Status"
                >
                  <MenuItem value="all">All Applications</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="under_review">Under Review</MenuItem>
                  <MenuItem value="approved">Approved</MenuItem>
                  <MenuItem value="rejected">Rejected</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Applicant</TableCell>
                  <TableCell>Destination</TableCell>
                  <TableCell>Visa Type</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Priority</TableCell>
                  <TableCell>Assigned Date</TableCell>
                  <TableCell>Last Contact</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredApplications.map((app) => (
                  <TableRow key={app.id}>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          {app.applicantName}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {app.clientEmail}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{app.destination}</TableCell>
                    <TableCell>{app.visaType}</TableCell>
                    <TableCell>
                      <Select
                        value={app.status}
                        onChange={(e) => handleUpdateStatus(app.id, e.target.value)}
                        size="small"
                      >
                        <MenuItem value="pending">Pending</MenuItem>
                        <MenuItem value="under_review">Under Review</MenuItem>
                        <MenuItem value="approved">Approved</MenuItem>
                        <MenuItem value="rejected">Rejected</MenuItem>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={app.priority} 
                        color={app.priority === 'high' ? 'error' : app.priority === 'medium' ? 'warning' : 'info'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{new Date(app.assignedAt).toLocaleDateString()}</TableCell>
                    <TableCell>{new Date(app.lastContact).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <IconButton
                        onClick={() => {
                          setSelectedApp(app);
                          setAppDetailOpen(true);
                        }}
                        color="primary"
                      >
                        <Visibility />
                      </IconButton>
                      <IconButton
                        onClick={() => {
                          setSelectedApp(app);
                          setContactModalOpen(true);
                        }}
                        color="info"
                      >
                        <Message />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Tasks Tab */}
      {currentTab === 1 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Paper elevation={0} sx={{ p: 3, bgcolor: 'rgba(15, 23, 42, 0.6)', border: '1px solid rgba(255, 255, 255, 0.08)', borderRadius: 2 }}>
              <Typography variant="h6" mb={3}>Today's Tasks</Typography>
              <List>
                {tasks.map((task) => (
                  <ListItem 
                    key={task.id}
                    sx={{ 
                      border: '1px solid rgba(255, 255, 255, 0.08)', 
                      borderRadius: 2, 
                      mb: 1,
                      bgcolor: task.completed ? 'rgba(255, 255, 255, 0.04)' : 'rgba(15, 23, 42, 0.6)'
                    }}
                  >
                    <ListItemIcon>
                      <IconButton
                        onClick={() => handleCompleteTask(task.id)}
                        color={task.completed ? 'success' : 'default'}
                      >
                        <CheckCircle />
                      </IconButton>
                    </ListItemIcon>
                    <ListItemText
                      primary={task.description}
                      secondary={`Due: ${new Date(task.dueDate).toLocaleDateString()} | Priority: ${task.priority}`}
                      sx={{ textDecoration: task.completed ? 'line-through' : 'none' }}
                    />
                    <Chip 
                      label={task.type} 
                      size="small"
                      color="primary"
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Paper elevation={0} sx={{ p: 3, bgcolor: 'rgba(15, 23, 42, 0.6)', border: '1px solid rgba(255, 255, 255, 0.08)', borderRadius: 2 }}>
              <Typography variant="h6" mb={2}>Performance Summary</Typography>
              <Box mb={2}>
                <Typography variant="body2" color="textSecondary">Average Processing Time</Typography>
                <Typography variant="h5" color="primary.main">{stats.averageProcessingTime}</Typography>
              </Box>
              <Box mb={2}>
                <Typography variant="body2" color="textSecondary">Success Rate</Typography>
                <Typography variant="h5" color="success.main">{stats.successRate}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="textSecondary">Clients Satisfaction</Typography>
                <Typography variant="h5" color="info.main">4.8/5.0</Typography>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Communication Tab */}
      {currentTab === 2 && (
        <Paper elevation={0} sx={{ p: 3, bgcolor: 'rgba(15, 23, 42, 0.6)', border: '1px solid rgba(255, 255, 255, 0.08)', borderRadius: 2 }}>
          <Typography variant="h6" mb={3}>Client Communication Center</Typography>
          <Grid container spacing={3}>
            {applications.slice(0, 6).map((app) => (
              <Grid item xs={12} sm={6} md={4} key={app.id}>
                <Card sx={{ border: '1px solid rgba(255, 255, 255, 0.08)', bgcolor: 'rgba(15, 23, 42, 0.6)' }}>
                  <CardContent>
                    <Box display="flex" alignItems="center" mb={2}>
                      <Avatar sx={{ mr: 2, bgcolor: '#1273E5' }}>
                        {app.applicantName.charAt(0)}
                      </Avatar>
                      <Box>
                        <Typography variant="body1" fontWeight="bold">
                          {app.applicantName}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {app.destination} Visa
                        </Typography>
                      </Box>
                    </Box>
                    
                    <Box display="flex" alignItems="center" mb={1}>
                      <Email sx={{ fontSize: 16, mr: 1, color: 'textSecondary' }} />
                      <Typography variant="body2">{app.clientEmail}</Typography>
                    </Box>
                    
                    <Box display="flex" alignItems="center" mb={2}>
                      <Phone sx={{ fontSize: 16, mr: 1, color: 'textSecondary' }} />
                      <Typography variant="body2">{app.clientPhone}</Typography>
                    </Box>
                    
                    <Box display="flex" gap={1}>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Message />}
                        onClick={() => {
                          setSelectedApp(app);
                          setContactModalOpen(true);
                        }}
                      >
                        Contact
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Phone />}
                        href={`tel:${app.clientPhone}`}
                      >
                        Call
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}

      {/* Application Detail Modal */}
      <Dialog open={appDetailOpen} onClose={() => setAppDetailOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Application Details - {selectedApp?.applicantName}</DialogTitle>
        <DialogContent>
          {selectedApp && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="textSecondary">Destination</Typography>
                <Typography variant="body1">{selectedApp.destination}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="textSecondary">Visa Type</Typography>
                <Typography variant="body1">{selectedApp.visaType}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="textSecondary">Status</Typography>
                <Chip label={selectedApp.status} color="primary" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="textSecondary">Priority</Typography>
                <Chip label={selectedApp.priority} color="warning" />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" color="textSecondary">Documents</Typography>
                <Box display="flex" gap={1} mt={1}>
                  {selectedApp.documents.map((doc, index) => (
                    <Chip key={index} label={doc} icon={<AttachFile />} />
                  ))}
                </Box>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAppDetailOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Contact Client Modal */}
      <Dialog open={contactModalOpen} onClose={() => setContactModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Contact Client - {selectedApp?.applicantName}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Message"
            value={contactMessage}
            onChange={(e) => setContactMessage(e.target.value)}
            sx={{ mt: 2 }}
            placeholder="Enter your message to the client..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setContactModalOpen(false)}>Cancel</Button>
          <Button onClick={handleContactClient} variant="contained">Send Message</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AgentDashboard;
